/**
 * What Nira reads in a question as it is typed: the sections and the Act they sit in, rules, forms,
 * notifications and circulars, citations and case names, the year, dates, rupees and the State.
 * Shown under the composer as one quiet line (QuestionReads). It runs in the browser on every
 * keystroke, so it is code: patterns and a table, never a model call.
 *
 * It only says what the question names. It never works out a date or an amount: the research does
 * that, from the law (backend nira/law).
 */
import { STATES } from "../readerContext"

export type ReadKind =
  | "section" | "rule" | "form" | "notification" | "citation" | "case"
  | "year" | "date" | "amount" | "state" | "forum" | "act" | "outside"

export interface Read {
  kind: ReadKind
  /** One per thing named: "s:cgst:74", "fy:2019-20", "amt:4510000". */
  key: string
  /** As shown: "s.74 · CGST Act", "FY 2019-20", "₹45,10,000". */
  label: string
  /** What it is, when Nira knows: "demand where there was fraud or suppression". */
  note?: string
  /** Where it sits in the question (the first time). */
  start: number
}

// --- the Acts ---------------------------------------------------------------------------------------

type ActKey = "cgst" | "igst" | "sgst" | "utgst" | "cess"
const ACT_NAME: Record<ActKey, string> = {
  cgst: "CGST Act", igst: "IGST Act", sgst: "SGST Act", utgst: "UTGST Act", cess: "GST Compensation Cess Act",
}
const ACT_RX: ReadonlyArray<readonly [ActKey, RegExp]> = [
  ["igst", /\b(?:IGST|Integrated\s+Goods\s+and\s+Services\s+Tax)\s+Act\b/i],
  ["utgst", /\bUTGST\s+Act\b/i],
  ["cess", /\bCompensation\s+(?:to\s+States\s+)?Cess\b/i],
  ["sgst", /\b(?:SGST|State\s+Goods\s+and\s+Services\s+Tax)\s+Act\b/i],
  ["cgst", /\b(?:CGST|Central\s+Goods\s+and\s+Services\s+Tax)\s+Act\b/i],
]

/** The headings of the sections a reader is most likely to name. Absent ones still show, by number. */
const CGST: Record<string, string> = {
  "2": "definitions",
  "7": "scope of supply",
  "9": "levy and collection of tax",
  "9(3)": "reverse charge, notified supplies",
  "9(4)": "reverse charge, supplies from unregistered persons",
  "10": "composition levy",
  "12": "time of supply of goods",
  "13": "time of supply of services",
  "15": "value of a taxable supply",
  "16": "eligibility and conditions for input tax credit",
  "16(2)": "the conditions for input tax credit",
  "16(2)(aa)": "credit only if the invoice is in GSTR-2B",
  "16(2)(b)": "credit only if the goods or services are received",
  "16(2)(c)": "credit only if the tax is actually paid to the Government",
  "16(4)": "time limit for taking input tax credit",
  "17": "apportionment of credit and blocked credits",
  "17(5)": "blocked credits",
  "17(5)(c)": "blocked credit, works contract for immovable property",
  "17(5)(d)": "blocked credit, construction on own account",
  "18": "credit in special circumstances",
  "22": "persons liable for registration",
  "29": "cancellation or suspension of registration",
  "31": "tax invoice",
  "34": "credit and debit notes",
  "37": "details of outward supplies (GSTR-1)",
  "39": "returns",
  "49": "payment of tax, interest and penalty",
  "50": "interest on delayed payment",
  "54": "refund",
  "61": "scrutiny of returns",
  "62": "assessment of non-filers",
  "63": "assessment of unregistered persons",
  "65": "audit by tax authorities",
  "67": "inspection, search and seizure",
  "69": "power to arrest",
  "70": "summons",
  "73": "demand where there was no fraud or suppression",
  "74": "demand where there was fraud or suppression",
  "74A": "demand, from FY 2024-25",
  "75": "general provisions on demands",
  "75(2)": "a s.74 demand where fraud is not proved on appeal",
  "76": "tax collected but not paid",
  "79": "recovery of tax",
  "83": "provisional attachment",
  "107": "appeal to the Appellate Authority",
  "107(6)": "pre-deposit for an appeal",
  "112": "appeal to the Appellate Tribunal",
  "122": "penalty for certain offences",
  "125": "general penalty",
  "126": "general rules for penalties",
  "127": "power to impose penalty",
  "128A": "waiver of interest and penalty, FY 2017-18 to 2019-20",
  "129": "detention and release of goods in transit",
  "130": "confiscation of goods or conveyance",
  "132": "punishment for certain offences",
  "140": "transitional credit",
  "155": "burden of proof",
  "160": "proceedings not invalid for certain mistakes",
  "168A": "extension of time limits",
  "169": "service of notices",
}
const IGST: Record<string, string> = {
  "5": "levy and collection of integrated tax",
  "7": "inter-State supply",
  "8": "intra-State supply",
  "10": "place of supply of goods",
  "12": "place of supply of services, both in India",
  "13": "place of supply of services, one outside India",
  "16": "zero-rated supply",
  "20": "the CGST Act applied to integrated tax",
}
const RULES: Record<string, string> = {
  "28": "value between related persons",
  "36(4)": "credit capped to GSTR-2B",
  "37": "reversal when the supplier is not paid in 180 days",
  "37A": "reversal when the supplier has not paid the tax",
  "42": "reversal of credit on inputs for exempt supplies",
  "43": "reversal of credit on capital goods",
  "86A": "blocking of the electronic credit ledger",
  "86B": "cash payment of 1% of output tax",
  "89": "refund application",
  "96": "refund of IGST paid on exports",
  "138": "e-way bill",
  "142": "notice and order forms (DRC)",
  "142(1A)": "the pre-notice intimation (DRC-01A)",
}
const FORMS: Record<string, string> = {
  "DRC-01": "show cause notice (summary)",
  "DRC-01A": "intimation before a notice",
  "DRC-01B": "difference between GSTR-1 and GSTR-3B",
  "DRC-01C": "difference between GSTR-2B and GSTR-3B",
  "DRC-03": "voluntary payment",
  "DRC-06": "reply to a notice",
  "DRC-07": "summary of an order",
  "DRC-08": "rectification of an order",
  "ASMT-10": "scrutiny notice",
  "ASMT-11": "reply to a scrutiny notice",
  "ASMT-12": "scrutiny closed",
  "APL-01": "appeal to the Appellate Authority",
  "APL-05": "appeal to the Tribunal",
  "MOV-07": "notice for goods detained",
  "MOV-09": "order for goods detained",
  "REG-17": "notice to cancel registration",
  "REG-31": "intimation to suspend registration",
  "GSTR-1": "outward supplies",
  "GSTR-2A": "inward supplies, as the suppliers filed",
  "GSTR-2B": "inward supplies, the credit statement",
  "GSTR-3B": "monthly return",
  "GSTR-9": "annual return",
  "GSTR-9C": "reconciliation statement",
  "PMT-03": "refund re-credit to the ledger",
}

// --- patterns ---------------------------------------------------------------------------------------

const SECTION = /\b(?:section|sec\.?|s\.|u\/s\.?|under\s+s)\s*(\d{1,3}[A-Z]{0,2})((?:\s*\(\s*[0-9]{1,2}[A-Z]?\s*\))*(?:\s*\(\s*[a-z]{1,4}\s*\))*)/gi
const RULE = /\b(?:rule|r\.)\s*(\d{1,3}[A-Z]{0,2})((?:\s*\(\s*[0-9]{1,2}[A-Z]?\s*\))*)/gi
const FORM = /\b(DRC|ASMT|APL|MOV|REG|GSTR|PMT)\s*-?\s*(\d{1,2}[A-C]?)\b/gi
const NOTIFICATION = /\bnotification\s+(?:no\.?\s*)?(\d{1,3}\/\d{4})(?:\s*[-–]\s*(Central|Integrated|Union\s+Territory|State)\s+Tax(?:\s*\(Rate\))?)?/gi
const CIRCULAR = /\bcircular\s+(?:no\.?\s*)?(\d{1,3}\/\d{1,3}\/\d{4})(?:\s*[-–]\s*GST)?/gi
const CITATIONS: ReadonlyArray<RegExp> = [
  /\b\d{4}\s+INSC\s+\d+\b/gi,
  /\(\d{4}\)\s*\d+\s*SCC\s+\d+/gi,
  /\b\d{4}\s*\(\d+\)\s*(?:G\.?S\.?T\.?L\.?|TMI|E\.?L\.?T\.?|S\.?T\.?R\.?)\s*\d+(?:\s*\([A-Za-z.\s]+\))?/gi,
  /\bW\.?P\.?\s*(?:\(C\)\s*)?(?:No\.?\s*)?\d+\s*(?:of|\/)\s*\d{4}/gi,
]
// "Suncraft Energy v. Union of India", "Safari Retreats vs Chief Commissioner"
const CASE = /\b((?:[A-Z][\w.&'-]*\s+){0,4}[A-Z][\w.&'-]*)\s+(?:v\.|vs\.?|versus)\s+((?:[A-Z][\w.&'-]*(?:\s+(?:of|and|the|&)(?=\s+[A-Z]))?\s*){1,6})/g
const KNOWN_CASES: ReadonlyArray<readonly [RegExp, string]> = [
  [/\bSafari\s+Retreats\b/i, "Safari Retreats"],
  [/\bMohit\s+Minerals\b/i, "Mohit Minerals"],
  [/\bSuncraft\s+Energy\b/i, "Suncraft Energy"],
  [/\bEcom\s+Gill\s+Coffee\b/i, "Ecom Gill Coffee"],
  [/\bArise\s+India\b/i, "Arise India"],
  [/\bBharti\s+Airtel\b/i, "Bharti Airtel"],
  [/\bKamlakshi\s+Finance\b/i, "Kamlakshi Finance"],
]
const YEAR = /\b(?:FY|F\.Y\.|financial\s+year|for\s+the\s+year|year)\s*(20\d{2})\s*[-–/]\s*(20)?(\d{2})\b|\b(20\d{2})\s*[-–]\s*(\d{2})\b(?!\s*[-–/]\s*\d)/gi
const MONTHS = "jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec"
const DATE = new RegExp(
  `\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTHS})[a-z]*\\.?,?\\s+(20\\d{2})\\b|\\b(\\d{1,2})[./-](\\d{1,2})[./-](20\\d{2})\\b|\\b(20\\d{2})-(\\d{2})-(\\d{2})\\b`,
  "gi",
)
const AMOUNT = /(?:₹|\bRs\.?|\bINR)\s*([\d,]+(?:\.\d+)?)\s*(crores?|cr\.?|lakhs?|lacs?|L\b)?|\b([\d,]+(?:\.\d+)?)\s*(crores?|cr\.?|lakhs?|lacs?)\b/gi
const FORUMS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\bSupreme\s+Court\b|\bSC\b(?=\s+(?:held|ruled|said|in))/i, "Supreme Court"],
  [/\bGSTAT\b|\bGST\s+Appellate\s+Tribunal\b/i, "GST Appellate Tribunal"],
  [/\bCESTAT\b/i, "CESTAT"],
  [/\bAAAR\b/i, "Appellate Authority for Advance Ruling"],
  [/\bAAR\b|\badvance\s+ruling\b/i, "Authority for Advance Ruling"],
]
// A High Court by its seat or its State: "Madras HC", "Delhi High Court".
const HC_SEATS: Record<string, string> = {
  madras: "Tamil Nadu", bombay: "Maharashtra", calcutta: "West Bengal", allahabad: "Uttar Pradesh",
  delhi: "Delhi", gujarat: "Gujarat", karnataka: "Karnataka", kerala: "Kerala", telangana: "Telangana",
  "andhra pradesh": "Andhra Pradesh", patna: "Bihar", orissa: "Odisha", orrisa: "Odisha", odisha: "Odisha",
  rajasthan: "Rajasthan", "punjab and haryana": "Punjab", "madhya pradesh": "Madhya Pradesh",
  jharkhand: "Jharkhand", chhattisgarh: "Chhattisgarh", gauhati: "Assam", uttarakhand: "Uttarakhand",
  "himachal pradesh": "Himachal Pradesh",
}
const HC = new RegExp(`\\b(${Object.keys(HC_SEATS).join("|")})\\s+(?:HC|High\\s+Court)\\b`, "gi")
const GSTIN = /\b(\d{2})[A-Z]{5}\d{4}[A-Z][0-9A-Z]Z[0-9A-Z]\b/g
// Laws outside GST: said early, so the reader is not surprised by the answer.
const OUTSIDE: ReadonlyArray<readonly [RegExp, string]> = [
  [/\bincome[\s-]?tax\b|\bIT\s+Act\b/i, "Income-tax"],
  [/\bCompanies\s+Act\b/i, "Companies Act"],
  [/\bFEMA\b/i, "FEMA"],
  [/\bPMLA\b/i, "PMLA"],
]
const PRE_GST: ReadonlyArray<readonly [RegExp, string]> = [
  [/\bservice\s+tax\b|\bFinance\s+Act,?\s+1994\b/i, "Service tax (Finance Act, 1994)"],
  [/\bcentral\s+excise\b/i, "Central Excise"],
  [/\bcustoms\s+act\b/i, "Customs Act"],
  [/\bVAT\b|\bvalue\s+added\s+tax\b/i, "VAT"],
]

// --- helpers ----------------------------------------------------------------------------------------

const STATE_NAMES = STATES.filter(([c]) => c !== "00")
const STATE_BY_CODE = new Map(STATE_NAMES)
const STATE_RX = new RegExp(`\\b(${STATE_NAMES.map(([, n]) => n.replace(/ /g, "\\s+")).sort((a, b) => b.length - a.length).join("|")})\\b`, "gi")

const squash = (s: string) => s.replace(/\s+/g, "")
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** "45,10,000" in the Indian grouping. */
export const inrGroup = (n: number) => Math.round(n).toLocaleString("en-IN")

function rupees(digits: string, unit: string | undefined): number | null {
  const n = Number(digits.replace(/,/g, ""))
  if (!Number.isFinite(n) || n <= 0) return null
  const u = (unit || "").toLowerCase()
  if (u.startsWith("cr")) return n * 1e7
  if (u.startsWith("l")) return n * 1e5
  return n
}

/** Which Act a section belongs to: the Act named right after it, else the one named in the question, else CGST. */
function actFor(text: string, end: number, fallback: ActKey): ActKey {
  const after = text.slice(end, end + 48)
  for (const [key, rx] of ACT_RX) if (new RegExp(`^\\s*(?:of\\s+(?:the\\s+)?)?${rx.source}`, rx.flags).test(after)) return key
  return fallback
}

// --- the read ---------------------------------------------------------------------------------------

/** Everything the question names, in the order it names them; each thing once. */
export function readQuestion(text: string): Read[] {
  if (!text || text.trim().length < 3) return []
  const out: Read[] = []
  const seen = new Set<string>()
  const add = (r: Read) => {
    if (seen.has(r.key)) return
    seen.add(r.key)
    out.push(r)
  }
  const each = (rx: RegExp, fn: (m: RegExpExecArray) => void) => {
    const re = new RegExp(rx.source, rx.flags.includes("g") ? rx.flags : `${rx.flags}g`)
    let m: RegExpExecArray | null
    while ((m = re.exec(text))) {
      fn(m)
      if (m[0].length === 0) re.lastIndex += 1
    }
  }

  // The Act the question is about, when it names one; sections without their own Act fall to it.
  let questionAct: ActKey = "cgst"
  for (const [key, rx] of ACT_RX) {
    const m = rx.exec(text)
    if (m) {
      add({ kind: "act", key: `act:${key}`, label: ACT_NAME[key], start: m.index })
      if (key !== "sgst") questionAct = key
    }
  }

  each(SECTION, (m) => {
    const num = m[1].toUpperCase()
    const sub = squash(m[2] || "").toLowerCase().replace(/\(([0-9]+)([a-z]?)\)/g, (_, d: string, l: string) => `(${d}${l.toUpperCase()})`)
    const full = `${num}${sub}`
    const act = actFor(text, m.index + m[0].length, questionAct)
    const table = act === "igst" ? IGST : act === "cgst" || act === "sgst" || act === "utgst" ? CGST : {}
    // The most specific heading Nira knows: 16(2)(c), else 16(2), else 16.
    let note: string | undefined
    for (let s = full; s; s = s.replace(/\([^()]*\)$/, "")) {
      if (table[s]) { note = table[s]; break }
      if (!/\)$/.test(s)) break
    }
    add({ kind: "section", key: `s:${act}:${full}`, label: `s.${full} · ${ACT_NAME[act]}`, note, start: m.index })
  })
  each(RULE, (m) => {
    const num = `${m[1].toUpperCase()}${squash(m[2] || "")}`
    const note = RULES[num] ?? RULES[m[1].toUpperCase()]
    add({ kind: "rule", key: `r:${num}`, label: `Rule ${num} · CGST Rules`, note, start: m.index })
  })
  each(FORM, (m) => {
    const form = `${m[1].toUpperCase()}-${m[2].toUpperCase()}`
    if (!FORMS[form] && m[1].toUpperCase() !== "GSTR" && m[1].toUpperCase() !== "DRC") return
    add({ kind: "form", key: `f:${form}`, label: form, note: FORMS[form], start: m.index })
  })
  each(NOTIFICATION, (m) => {
    const series = m[2] ? ` ${m[2].replace(/\s+/g, " ")} Tax` : ""
    add({ kind: "notification", key: `n:${m[1]}${series}`, label: `Notification ${m[1]}${series}`, start: m.index })
  })
  each(CIRCULAR, (m) => add({ kind: "notification", key: `c:${m[1]}`, label: `Circular ${m[1]}`, start: m.index }))
  for (const rx of CITATIONS) each(rx, (m) => add({ kind: "citation", key: `cite:${squash(m[0]).toLowerCase()}`, label: m[0].replace(/\s+/g, " "), start: m.index }))
  each(CASE, (m) => {
    // A sentence's first words are not part of the name: "Is Suncraft Energy v. Union" is Suncraft Energy.
    const a = m[1].trim().replace(/^(?:(?:Is|Does|Did|Can|In|After|Under|The|What|Whether|Per|As|Following|Since|Was|Has)\s+)+/, "")
    const b = m[2].trim().replace(/\s+(?:held|ruled|said|in|on|and|is|was|that|still|good|law)\b.*$/i, "")
    if (a.length < 3 || b.length < 3) return
    add({ kind: "case", key: `case:${a.toLowerCase()}`, label: `${a} v. ${b}`, start: m.index })
  })
  for (const [rx, name] of KNOWN_CASES) {
    const m = rx.exec(text)
    if (m && !out.some((r) => r.kind === "case" && r.label.toLowerCase().includes(name.toLowerCase()))) {
      add({ kind: "case", key: `case:${name.toLowerCase()}`, label: name, start: m.index })
    }
  }
  each(YEAR, (m) => {
    const y = Number(m[1] || m[4])
    const tail = Number(m[3] || m[5])
    if (tail !== (y + 1) % 100 || y < 2017 || y > 2035) return
    const fy = `${y}-${String(tail).padStart(2, "0")}`
    add({ kind: "year", key: `fy:${fy}`, label: `FY ${fy}`, start: m.index })
  })
  each(DATE, (m) => {
    let d: number, mo: number, y: number
    if (m[1]) { d = Number(m[1]); mo = MONTH_NAMES.findIndex((n) => n.toLowerCase() === m[2].toLowerCase().slice(0, 3)) + 1; y = Number(m[3]) }
    else if (m[4]) { d = Number(m[4]); mo = Number(m[5]); y = Number(m[6]) }
    else { y = Number(m[7]); mo = Number(m[8]); d = Number(m[9]) }
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return
    const label = `${d} ${MONTH_NAMES[mo - 1]} ${y}`
    add({ kind: "date", key: `d:${y}-${mo}-${d}`, label, start: m.index })
  })
  each(AMOUNT, (m) => {
    const n = rupees(m[1] ?? m[3], m[2] ?? m[4])
    if (n === null || n < 100) return
    add({ kind: "amount", key: `amt:${n}`, label: `₹${inrGroup(n)}`, start: m.index })
  })
  for (const [rx, name] of FORUMS) {
    const m = rx.exec(text)
    if (m) add({ kind: "forum", key: `forum:${name}`, label: name, start: m.index })
  }
  each(HC, (m) => {
    const seat = m[1].toLowerCase().replace(/\s+/g, " ")
    const name = seat.replace(/\b\w/g, (c) => c.toUpperCase())
    // The court's State is not the reader's: it is said on the court, never as the question's State.
    add({ kind: "forum", key: `hc:${seat}`, label: `${name} High Court`, note: `binds in ${HC_SEATS[seat]}`, start: m.index })
  })
  each(STATE_RX, (m) => {
    const name = STATE_NAMES.find(([, n]) => n.toLowerCase() === m[1].toLowerCase().replace(/\s+/g, " "))?.[1]
    if (name) add({ kind: "state", key: `st:${name}`, label: name, start: m.index })
  })
  each(GSTIN, (m) => {
    const name = STATE_BY_CODE.get(m[1])
    if (name) add({ kind: "state", key: `st:${name}`, label: name, note: "from the GSTIN", start: m.index })
  })
  for (const [rx, name] of PRE_GST) {
    const m = rx.exec(text)
    if (m) add({ kind: "act", key: `pre:${name}`, label: name, note: "before GST; Nira applies it by analogy", start: m.index })
  }
  for (const [rx, name] of OUTSIDE) {
    const m = rx.exec(text)
    if (m) add({ kind: "outside", key: `out:${name}`, label: name, note: "outside Nira, which answers on GST only", start: m.index })
  }
  return out.sort((a, b) => a.start - b.start)
}
